import { useEffect, useRef, type ReactNode } from 'react';

import { Button } from './button';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Hide the visible title (still announced). */
  hideTitle?: boolean;
}

/** Native <dialog>: focus trap, Esc, and backdrop come from the browser. */
export function Dialog({ open, onClose, title, children, hideTitle = false }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog ref={ref} className="dialog" onClose={onClose} aria-labelledby="dialog-title">
      <div className="dialog__body">
        <Button variant="ghost" className="dialog__close" onClick={onClose} aria-label="Close">
          ✕
        </Button>
        <h3 id="dialog-title" className={hideTitle ? 'visually-hidden' : ''}>
          {title}
        </h3>
        {open && children}
      </div>
    </dialog>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  cancelLabel?: string;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} title={title}>
      <p style={{ marginTop: '1rem' }}>{body}</p>
      <div className="dialog__actions">
        <Button variant="secondary" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button onClick={onConfirm}>{confirmLabel}</Button>
      </div>
    </Dialog>
  );
}
